import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { Settings } from "lucide-react";
import { Button, Combobox, Field, Input, Label, Spinner } from "@/components/ui";
import { ConfigContext } from "@/contexts/appContexts";
import { CoachSettings } from "@/interfaces";
import {
  Blog,
  BlogHome,
  BlogHomeServices,
  BlogServices,
  ContentPageTheme,
  ContentPageThemeServices,
  Media,
} from "@/services/bedrock";
import { getValidFiles, saveElementToFirebase, URLS, usePlayerSettings, useSocialCoachSession } from "@/utils";
import { BlogHomePreview, BlogHomePreviewHandle, BlogHomePreviewPart } from "./BlogHomePreview";
import { BlogsList } from "./BlogsList";
import { themeForPreview } from "./blogPreviewPayload";
import { withCurrentThemeOption } from "./blogBody";
import {
  BLOG_HOME_SLUG_TAKEN_ERROR,
  BlogHomeDraft,
  blogHomePreviewUrl,
  blogHomeSlugFormatError,
  isBlogHomeSlugTakenError,
  profileMediaUri,
  toBlogHomeDraft,
} from "./blogHomePreviewPayload";
import { copyShareUrl } from "./copyShareUrl";

export const PlayerBlogHomePage: React.FC = () => {
  const { firebase } = useContext(ConfigContext);
  const { isPlayer, playerId } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(playerId ?? undefined);
  const canChangeTheme = !isPlayer || !!hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<BlogHomePreviewHandle>(null);

  const [page, setPage] = useState<BlogHome>();
  const [draft, setDraft] = useState<BlogHomeDraft>();
  const [publishedBlogs, setPublishedBlogs] = useState<Blog[]>([]);
  const [themes, setThemes] = useState<ContentPageTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    BlogHomeServices.getBlogHome()
      .then(home => {
        if (cancelled) return;
        setPage(home);
        setDraft(toBlogHomeDraft(home));
      })
      .catch(error => {
        console.error(error);
        toast.error("Failed to load blog home settings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    BlogServices.getBlogs({
      status: "PUBLISHED",
      sort: "published_at",
      order: "desc",
      perPage: 50,
    })
      .then(blogsPage => {
        if (!cancelled) setPublishedBlogs(blogsPage.content ?? []);
      })
      .catch(console.error);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canChangeTheme) return;
    ContentPageThemeServices.getPageThemes({ perPage: 100 })
      .then(response => setThemes(response.content))
      .catch(console.error);
  }, [canChangeTheme]);

  const themeItems = useMemo(
    () =>
      withCurrentThemeOption(
        themes.map(theme => ({ id: theme.id, label: theme.name })),
        draft?.themeId
      ),
    [themes, draft?.themeId]
  );

  const selectedTheme = useMemo(
    () => (draft?.themeId == null ? null : themeItems.find(item => item.id === draft.themeId) ?? null),
    [draft?.themeId, themeItems]
  );

  const dirty = useMemo(() => {
    if (!page || !draft) return false;
    return (
      (draft.title.trim() || null) !== (page.title || null) ||
      (draft.subtitle.trim() || null) !== (page.subtitle || null) ||
      draft.slug.trim() !== (page.slug || "") ||
      (profileMediaUri(draft.profileMedia) || null) !== (profileMediaUri(page.profileMedia) || null) ||
      (draft.themeId ?? null) !== (page.contentPageThemeId ?? null)
    );
  }, [page, draft]);

  const previewImageUrl = profileMediaUri(draft?.profileMedia) || page?.defaultProfileImageUrl;
  const previewTitle = draft?.title.trim() || page?.defaultTitle || "Your name";
  const previewTheme = useMemo(
    () =>
      draft ? themeForPreview(draft, themes, page?.effectiveContentPageTheme ?? page?.contentPageTheme) : undefined,
    [draft, themes, page?.effectiveContentPageTheme, page?.contentPageTheme]
  );
  const shareUrl = page && draft ? blogHomePreviewUrl(page, draft) : page?.url || "";

  const focusPart = (part: BlogHomePreviewPart) => {
    previewRef.current?.scrollToPart(part);
  };

  const blurPart = () => {
    previewRef.current?.highlightPart(null);
  };

  const save = async () => {
    if (!draft) return;
    const formatError = blogHomeSlugFormatError(draft.slug);
    if (formatError) {
      setSlugError(formatError);
      return;
    }

    setSaving(true);
    try {
      const next = await BlogHomeServices.updateBlogHome({
        title: draft.title.trim() || null,
        subtitle: draft.subtitle.trim() || null,
        slug: draft.slug.trim(),
        profileMedia: draft.profileMedia,
        themeId: draft.themeId,
      });
      setPage(next);
      setDraft(toBlogHomeDraft(next));
      setSlugError(null);
      toast.success("Blog home saved");
    } catch (error) {
      console.error(error);
      if (isBlogHomeSlugTakenError(error)) {
        setSlugError(BLOG_HOME_SLUG_TAKEN_ERROR);
        return;
      }
      toast.error("Failed to save blog home settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const uri = await saveElementToFirebase(firebase, "contentPages/blogHome", file, uuidv4());
      const uploaded: Media = {
        id: -1,
        category: "IMAGE",
        uri,
        type: file.type,
        extension: file.type.split("/")[1] || "jpg",
        sortOrder: 0,
      };
      setDraft(current => (current ? { ...current, profileMedia: [uploaded] } : current));
      previewRef.current?.scrollToPart("photo");
    } catch (error) {
      console.error(error);
      toast.error("Could not upload that image");
    } finally {
      setUploading(false);
    }
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = getValidFiles(Array.from(event.target.files || [])).find(item => item.type.startsWith("image/"));
    if (file) void uploadImage(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-4">
      {loading || !draft || !page ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col xl:flex-row gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-6">
            <form
              className="flex flex-col gap-4 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm"
              onSubmit={event => {
                event.preventDefault();
                void save();
              }}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={onFileSelected}
                />
                {previewImageUrl ? (
                  <img
                    src={previewImageUrl}
                    alt={previewTitle}
                    className="h-24 w-24 rounded-full object-cover ring-2 ring-border"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted text-lg font-semibold uppercase text-muted-foreground">
                    {previewTitle.slice(0, 2)}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Label>Profile image</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      loading={uploading}
                      onClick={() => {
                        focusPart("photo");
                        fileInputRef.current?.click();
                      }}
                    >
                      Upload image
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={draft.profileMedia.length === 0}
                      onClick={() => setDraft({ ...draft, profileMedia: [] })}
                    >
                      Use profile photo
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="blog-home-slug">URL slug</Label>
                <Input
                  id="blog-home-slug"
                  value={draft.slug}
                  placeholder="your-blog"
                  aria-invalid={slugError ? true : undefined}
                  onChange={event => {
                    const slug = event.target.value;
                    setDraft({ ...draft, slug });
                    setSlugError(current => (current ? blogHomeSlugFormatError(slug) : null));
                  }}
                />
                {slugError && <Field.Error>{slugError}</Field.Error>}
                {page.url && (
                  <p className="text-xs text-muted-foreground break-all">
                    Public address: {blogHomePreviewUrl(page, draft) || page.url}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="blog-home-title" className="text-indigo-900/70">
                  Title
                </Label>
                <Input
                  id="blog-home-title"
                  value={draft.title}
                  placeholder={page.defaultTitle || "Your name"}
                  className="h-11 border-indigo-100 bg-white text-lg font-semibold"
                  onFocus={() => focusPart("title")}
                  onBlur={blurPart}
                  onChange={event => setDraft({ ...draft, title: event.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="blog-home-subtitle">Subtitle</Label>
                <Input
                  id="blog-home-subtitle"
                  value={draft.subtitle}
                  placeholder="A short line under your title"
                  onFocus={() => focusPart("subtitle")}
                  onBlur={blurPart}
                  onChange={event => setDraft({ ...draft, subtitle: event.target.value })}
                />
              </div>

              {canChangeTheme && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="blog-home-theme">Theme</Label>
                  <div className="flex flex-row items-center gap-2">
                    <Combobox
                      id="blog-home-theme"
                      className="flex-1 min-w-0 w-full"
                      placeholder="Default Theme"
                      items={themeItems}
                      value={selectedTheme}
                      showClear
                      onValueChange={option => setDraft({ ...draft, themeId: option?.id ?? null })}
                      itemToStringLabel={option => option.label}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-lg"
                      aria-label="Open theme settings"
                      onClick={() => window.open(isPlayer ? URLS.player.mvpSettings : URLS.coach.mvpSettings, "_blank")}
                    >
                      <Settings className="size-5" />
                    </Button>
                  </div>
                </div>
              )}

              <Button type="submit" className="self-start" loading={saving} disabled={!dirty || saving || uploading}>
                Save
              </Button>
            </form>

            <BlogsList />
          </div>

          <div className="w-full min-w-0 xl:w-1/2 xl:self-start xl:sticky xl:top-4 xl:z-10">
            <BlogHomePreview
              ref={previewRef}
              home={page}
              draft={draft}
              blogs={publishedBlogs}
              theme={previewTheme}
              shareUrl={shareUrl || undefined}
              onCopyShareUrl={shareUrl ? () => copyShareUrl(shareUrl) : undefined}
              onOpenPage={() => window.open(shareUrl || page.url!, "_blank")}
              className="h-[min(70dvh,640px)] min-h-80 xl:h-[max(20rem,calc(100dvh-16rem))]"
            />
          </div>
        </div>
      )}
    </div>
  );
};
